'use client'

import { Avatar, Button } from '@chain-zen/ui'
import React from 'react'

import { StandardSection } from './standard-section'
import { VerificationBadge } from './verification-badge'
import { VerifyButton } from './verify-button'

interface ISectionProps {
  avatarVariant?: 'square' | 'basic'
  buttonInfo?: string
  buttonName?: string
  buttonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  children?: React.ReactNode
  description?: string
  isVerified?: boolean
  multipleFields?: { fieldName: string; value?: string }[]
  name?: string
  onClick?: () => void
  profilePhoto?: string
  sectionTitle: string
  showAvatarBadge?: boolean
  showProfilePhotoSection?: boolean
  singleField?: string
  updateStatus?: boolean
  verifyAction?: () => void
}

/**
 * Reusable section component for settings pages
 * Can be used in both workspace and user settings with consistent styling
 */
export const Section = (props: ISectionProps) => {
  const {
    avatarVariant = 'basic',
    buttonInfo,
    buttonName,
    buttonVariant = 'default',
    children,
    description,
    isVerified,
    multipleFields,
    name,
    onClick,
    profilePhoto,
    sectionTitle,
    showAvatarBadge = false,
    showProfilePhotoSection = false,
    singleField,
    updateStatus,
    verifyAction
  } = props

  return (
    <div className={`space-y-4 p-4`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{sectionTitle}</h3>
        <VerificationBadge isVerified={isVerified} singleField={singleField} />
        {!!verifyAction && (
          <VerifyButton
            singleField={singleField}
            isVerified={isVerified}
            verifyAction={verifyAction}
            updateStatus={updateStatus}
          />
        )}
      </div>

      {!!description && <p className="text-sm text-muted-foreground">{description}</p>}

      {showProfilePhotoSection ? (
        <ProfilePhotoSection
          profilePhoto={profilePhoto || ''}
          avatarVariant={avatarVariant}
          name={name}
          showAvatarBadge={showAvatarBadge}
          buttonName={buttonName}
          buttonVariant={buttonVariant}
          onClick={onClick}
          buttonInfo={buttonInfo}
          children={children}
        />
      ) : (
        <StandardSection
          multipleFields={multipleFields}
          singleField={singleField}
          buttonName={buttonName}
          buttonVariant={buttonVariant}
          onClick={onClick}
          children={children}
        />
      )}
    </div>
  )
}

interface IProfilePhotoSectionProps {
  avatarVariant: 'square' | 'basic'
  buttonInfo?: string
  buttonName?: string
  buttonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  children?: React.ReactNode
  name?: string
  onClick?: () => void
  profilePhoto: string
  showAvatarBadge: boolean
}

/**
 * Profile photo section with avatar
 */
const ProfilePhotoSection = ({
  profilePhoto,
  avatarVariant,
  name,
  showAvatarBadge,
  buttonName,
  buttonVariant,
  onClick,
  buttonInfo,
  children
}: IProfilePhotoSectionProps) => (
  <div className="space-y-2">
    {!!buttonInfo && <p className="text-muted-foreground text-xs">{buttonInfo}</p>}
    <div className="flex flex-col sm:flex-row sm:items-end gap-4 justify-between">
      <div className="relative inline-block sm:mb-0">
        <Avatar variant={avatarVariant} src={profilePhoto} size="lg" alt={name || ''} name={name || ''} />
        {!!showAvatarBadge && (
          <span className="absolute bottom-0 right-0 h-4 w-4 bg-green-600 rounded-full border border-background"></span>
        )}
      </div>
      <div>
        {buttonName && onClick ? (
          <Button variant={buttonVariant} size="sm" onClick={onClick}>
            {buttonName}
          </Button>
        ) : (
          children
        )}
      </div>
    </div>
  </div>
)
